/**
 * Spec 084 (gap 084-1) — confirmation modal for rule deprecation.
 *
 * Shared between `RuleLibraryPage` and `RuleDetailPage`. Requires a reason
 * of at least 10 characters before submit — this matches the server-side
 * expectation that deprecation_reason is always meaningful (see
 * `src/rules/library.ts#deprecateRule`).
 */
import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';

interface DeprecateModalProps {
  ruleName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const MIN_REASON_LENGTH = 10;

export function DeprecateModal({
  ruleName,
  pending,
  onCancel,
  onConfirm,
}: DeprecateModalProps) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= MIN_REASON_LENGTH;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="deprecate-modal-title"
      data-testid="deprecate-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <div className="w-full max-w-lg rounded-lg bg-white p-6">
        <h2
          id="deprecate-modal-title"
          className="text-base font-semibold text-slate-900"
        >
          Deprecate rule
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          You are about to deprecate <strong>{ruleName}</strong>. This removes
          it from the active evaluation set. Provide a reason (at least{' '}
          {MIN_REASON_LENGTH} characters) for the audit trail.
        </p>

        <label className="mt-4 block text-sm">
          <span className="block text-slate-600 mb-1">Deprecation reason</span>
          <textarea
            data-testid="deprecate-modal-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            required
            minLength={MIN_REASON_LENGTH}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
            placeholder="e.g. Superseded by rule `title-first-100-words` which measures the same signal more robustly."
          />
        </label>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={pending} data-testid="deprecate-modal-cancel">
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!valid || pending}
            onClick={() => onConfirm(reason.trim())}
            data-testid="deprecate-modal-confirm"
          >
            {pending ? 'Deprecating…' : 'Deprecate'}
          </Button>
        </div>
      </div>
    </div>
  );
}
